import { Disposable, autoinject } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationController, ValidationControllerFactory, ValidationRules } from "aurelia-validation";
import { DueDiligenceApi } from "../../../../../apis/due-diligence.api";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { LookupValue, LookupValueRequest } from "@rightship-tech/common-ui/models/lookup.models";
import { LookupClientService } from "@rightship-tech/common-ui/services/lookups/lookup-client.service";
import { CustomValidationRenderer } from "@rightship-tech/common-ui/client-validation-system/custom-validation-renderer";
import { LookupValueQuery } from "@rightship-tech/common-ui/constants/lookup.constants";
import { Messages } from "@rightship-tech/common-ui/constants/event-messages.constants";

@autoinject
export class DueDiligenceStatusDialog {
    
    private _dueDiligenceId: number;
    private _dueDiligenceStatusId: number;
    private _validator: ValidationController;
    public vesselName: string;
    public internationalMaritimeOrganizationNumber: string;
    public europeanNumberOfIdentification: string;
    public vehicleIdentificationNumber: string;
    public dwt: number;
    public vesselType: string;
    public isDropdownOpen: boolean;
    public dueDiligenceStatuses: LookupValue[];
    public currentDueDiligenceStatus: LookupValue;
    public statusSnippet: string;
    public subscriptions: Disposable[] = [];

    constructor(private lookupClientService: LookupClientService, private dialogClientService: DialogClientService,
        private dueDiligenceApi: DueDiligenceApi, private eventAggregator: EventAggregator, 
        validationControllerFactory: ValidationControllerFactory, private notificationClientService: NotificationClientService) {

        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
        this.setValidationRules();
    }

    public async activate(model) {
        this._dueDiligenceId = model.dueDiligenceId;
        this._dueDiligenceStatusId = model.dueDiligenceStatusId;
        this.vesselName = model.vesselName;
        this.internationalMaritimeOrganizationNumber = model.internationalMaritimeOrganizationNumber;
        this.europeanNumberOfIdentification = model.europeanNumberOfIdentification;
        this.vehicleIdentificationNumber = model.vehicleIdentificationNumber;
        this.dwt = model.dwt;
        this.vesselType = model.vesselType;

        //Get due diligence statuses (Vet statuses)
        const request = new LookupValueRequest();
        request.parameters = "1,3";
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        const response = await this.lookupClientService.getLookup(request);
        this.dueDiligenceStatuses = response.activeValues;

        //Set current due diligence status
        this.currentDueDiligenceStatus = this.dueDiligenceStatuses.find(x => x.id == this._dueDiligenceStatusId);

        //Subscribe for document click
        this.subscriptions.push(this.eventAggregator.subscribe(Messages.documentClick,  () => {
            if (this.isDropdownOpen) {
                this.closeDropdown();
            }
        }));
    }

    public detached() {
        if (this.subscriptions) {
            this.subscriptions.forEach(x => x.dispose());
            this.subscriptions = [];
        }
    }

    //Validation rules
    public setValidationRules() {
        ValidationRules
            .ensure<DueDiligenceStatusDialog, string>(x => x.statusSnippet)
            .required()
            .on(this);
    }

    //Open dropdown
    public openDropdown(event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    //close dropdown
    public closeDropdown() {
        this.isDropdownOpen = false;
    }

    //Change due diligence status
    public changeDueDiligenceStatus(dueDiligenceStatus: LookupValue) {
        this.currentDueDiligenceStatus = dueDiligenceStatus;
    }

    //Close
    public close() {
        this.dialogClientService.hide();
    }

    //Submit
    public async submit() {
        const validationResult = await this._validator.validate();
        if (validationResult.valid) {

            //Change status
            try {
                await this.dueDiligenceApi.changeStatus(this._dueDiligenceId, "", this.statusSnippet, this.currentDueDiligenceStatus.id);
                this.close();
                this.notificationClientService.show("Due diligence status was successfully updated", "", NotificationType.Success);
            } 
            catch (error) {
                console.error("An error occurred while changing due diligence status", error);
                this.notificationClientService.show("An error occurred while changing due diligence status", "", NotificationType.Error);
            }
        }
    }
}
